import { createContext, ReactNode, useCallback, useRef } from 'react';
import { useRouter, useSafeContext, useServices } from 'core/common/hooks';
import { isDraftLink } from 'core/common/utils/isDraftLink';
import { splitBy } from 'core/common/utils/splitBy';
import {
  ATTRS_ANALYTICS_FUNNEL_LOCALE,
  ATTRS_ANALYTICS_FUNNEL_NAME,
  ATTRS_ANALYTICS_FUNNEL_SCREEN,
  ObservabilitySpan,
  SpanName,
} from '../entities';

export const ObservabilityContext = createContext<{
  trackScreenRendered: (screenId: string) => void;
  trackRedirectToNextScreen: (screenId: string) => void;
}>(undefined!);

type ObservabilityProviderProps = {
  children: ReactNode;
};

export const ObservabilityProvider = ({ children }: ObservabilityProviderProps) => {
  const { observabilitySystem } = useServices();
  const { asPath: path, locale = 'en' } = useRouter();

  const funnel = splitBy(path.split('/')[1], '?');

  const traces = useRef<Record<string, ObservabilitySpan>>({});

  // start open span and stop previous clicked
  const trackScreenRendered = useCallback(
    (screenId: string) => {
      if (isDraftLink(path)) return;

      const hasActiveSpan = !!traces.current[`screen_opened_${screenId}`];

      if (hasActiveSpan) {
        return;
      }

      const span = observabilitySystem.startSpan(SpanName.SCREEN_OPENED, {
        attributes: {
          [ATTRS_ANALYTICS_FUNNEL_SCREEN]: screenId,
          [ATTRS_ANALYTICS_FUNNEL_NAME]: funnel,
          [ATTRS_ANALYTICS_FUNNEL_LOCALE]: locale,
        },
      });
      span.addEvent('Pending');

      traces.current[`screen_opened_${screenId}`] = span;
    },
    [funnel, locale, observabilitySystem, path],
  );

  // stop open span and start new clicked
  const trackRedirectToNextScreen = useCallback(
    (screenId: string) => {
      if (isDraftLink(path)) return;

      const stopSpan = traces.current[`screen_opened_${screenId}`];

      if (stopSpan) {
        stopSpan.addEvent('Succeed');
        stopSpan.succeed();
      }
    },
    [path],
  );

  const contextValue = {
    trackScreenRendered,
    trackRedirectToNextScreen,
  };

  return (
    <ObservabilityContext.Provider value={contextValue}>{children}</ObservabilityContext.Provider>
  );
};

export const useObservability = () => {
  const context = useSafeContext(ObservabilityContext);

  return context;
};
